import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, ChevronDown, Database, Eye, Trash2, Download, Mail, Globe, Lock, Users, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SEOMeta } from '@/components/SEOMeta';
import { PageBreadcrumb } from '@/components/PageBreadcrumb';
import { Link } from 'react-router-dom';

const BREADCRUMBS = [{ label: 'Политика за поверителност', href: '/privacy' }];
const LAST_UPDATED = '27.06.2026';

const sections = [
  {
    id: 'controller',
    icon: Globe,
    title: '1. Администратор на лични данни',
    content: `Администратор на личните данни по смисъла на Регламент (ЕС) 2016/679 (GDPR) е:

portion.bg
Уебсайт: https://portion.bg
Имейл за контакт: info@portion.bg

За въпроси, свързани с обработването на Вашите лични данни, можете да се свържете с нас на посочения имейл адрес.`,
  },
  {
    id: 'data-collected',
    icon: Database,
    title: '2. Каква информация събираме',
    content: `2.1. Данни при регистрация
• Имейл адрес (задължителен)
• Пълно име (задължително)
• Парола (съхранява се само в криптиран вид — bcrypt хеш)

2.2. Данни при използване на услугата
• Физически данни, въведени в TDEE калкулатора: възраст, тегло, ръст, пол, ниво на активност
• Хранителни предпочитания: цел, тип диета, изключени храни
• Генерирани хранителни планове и история на плановете
• Данни за баланс и транзакции с кредити

2.3. Данни при плащане
Плащанията се обработват от Stripe, Inc. Ние не съхраняваме данни за платежни карти. Получаваме само информация за статуса на транзакцията (платено/неплатено) и ID на сесията.

2.4. Технически данни
• IP адрес (за сигурност и предотвратяване на злоупотреби)
• Тип на браузъра и операционна система
• Дата и час на достъп

2.5. Бисквитки (cookies)
Виж Раздел 7 за подробна информация относно бисквитките.`,
  },
  {
    id: 'purpose',
    icon: Eye,
    title: '3. Цел и правно основание за обработване',
    content: `Обработваме Вашите лични данни на следните правни основания:

3.1. Изпълнение на договор (чл. 6, пар. 1, б. „б" GDPR)
• Регистрация и управление на акаунта
• Генериране на персонализирани хранителни планове
• Обработване на плащания и управление на кредити
• Изпращане на трансакционни имейли (добре дошъл, потвърждение на плащане)

3.2. Законов интерес (чл. 6, пар. 1, б. „е" GDPR)
• Сигурност на платформата и предотвратяване на злоупотреби
• Изпращане на напомняния за незавършени поръчки (abandoned cart email)
• Подобряване на услугата

3.3. Законово задължение (чл. 6, пар. 1, б. „в" GDPR)
• Съхраняване на счетоводна документация`,
  },
  {
    id: 'retention',
    icon: Database,
    title: '4. Срок на съхранение на данните',
    content: `Съхраняваме Вашите данни за следните периоди:

• Данни на акаунта: до изтриване на акаунта + 30 дни гратисен период
• Хранителни планове: до изтриване на акаунта
• Данни за транзакции: 5 години (законово изискване за счетоводни документи)
• Технически логове: 90 дни
• Имейл кореспонденция: 2 години

След изтичане на посочените срокове, данните се изтриват автоматично или се анонимизират необратимо.`,
  },
  {
    id: 'rights',
    icon: ShieldCheck,
    title: '5. Вашите права по GDPR',
    content: `Като субект на данни, имате следните права:

Право на достъп (чл. 15 GDPR)
Можете да поискате копие от всички лични данни, които съхраняваме за Вас.

Право на коригиране (чл. 16 GDPR)
Можете да поискате коригиране на неточни или непълни данни.

Право на изтриване („право да бъдете забравени") (чл. 17 GDPR)
Можете да поискате изтриване на Вашите данни, когато те вече не са необходими за целите, за които са събрани.

Право на ограничаване на обработването (чл. 18 GDPR)
При определени обстоятелства можете да поискате ограничаване на обработването.

Право на преносимост (чл. 20 GDPR)
Можете да поискате данните Ви в структуриран, машинно четим формат (JSON/CSV).

Право на възражение (чл. 21 GDPR)
Можете да възразите срещу обработване, основано на законов интерес.

Право на оттегляне на съгласието
Когато обработването се основава на съгласие, можете да го оттеглите по всяко време.

Как да упражните правата си:
Изпратете искане на info@portion.bg. Ще отговорим в срок до 30 дни.

Право на жалба: КЗЛД (Комисия за защита на личните данни), www.cpdp.bg`,
  },
  {
    id: 'third-parties',
    icon: Users,
    title: '6. Трети страни и обработващи лични данни',
    content: `Работим с внимателно подбрани партньори, с които имаме сключени договори за обработка на данни:

Stripe, Inc. (Обработване на плащания)
• Предназначение: Обработка на картови плащания
• Данни: Payment session ID, имейл (за квитанции)
• Местоположение: САЩ (с Европейски стандартни клаузи - SCC)
• Политика за поверителност: https://stripe.com/privacy

Resend, Inc. (Трансакционни имейли)
• Предназначение: Изпращане на имейли (добре дошъл, потвърждения, напомняния)
• Данни: Имейл адрес, собствено име
• Местоположение: САЩ (с Европейски стандартни клаузи - SCC)
• Политика за поверителност: https://resend.com/privacy

Google (Gemini AI — генериране на планове)
• Предназначение: Генериране на хранителни планове с ИИ
• Данни: Хранителни предпочитания, калориен прием (анонимизирани)
• Политика за поверителност: https://policies.google.com/privacy

Ние НЕ продаваме или предоставяме Вашите данни на рекламни платформи или брокери на данни.`,
  },
  {
    id: 'cookies',
    icon: Lock,
    title: '7. Бисквитки (Cookies)',
    content: `Използваме следните видове бисквитки:

Задължителни бисквитки (винаги активни)
Тези бисквитки са необходими за функционирането на платформата и не могат да бъдат деактивирани:
• auth_token — сесия за вход (JWT токен, localStorage)
• gdpr_consent — запаметено GDPR решение (localStorage)

Функционални бисквитки
• quiz_data — временно съхранение на данните от TDEE калкулатора (localStorage, изчиства се след генериране на план)
• plan_cache — кеш на последния генериран план (localStorage)

Аналитични бисквитки (само при съгласие)
Към момента не използваме аналитични инструменти от трети страни.

Управление на бисквитките:
Можете да промените предпочитанията си по всяко време чрез настройките на браузъра или кликвайки на „Настройки за поверителност" в долния край на страницата.`,
  },
  {
    id: 'security',
    icon: Lock,
    title: '8. Сигурност на данните',
    content: `Прилагаме технически и организационни мерки за защита на Вашите данни:

• Паролите се съхраняват само като криптиран хеш (bcrypt)
• Комуникацията е криптирана чрез HTTPS (TLS 1.2+)
• Данните за плащания не се съхраняват от нас — обработват се директно от Stripe
• Достъпът до базата данни е ограничен само до необходими лица
• Редовно наблюдение за необичайни дейности

При установяване на нарушение на сигурността, което засяга Вашите данни, ще Ви уведомим в рамките на 72 часа.`,
  },
  {
    id: 'contact-dpo',
    icon: Mail,
    title: '9. Контакт и жалби',
    content: `За всякакви въпроси, свързани с поверителността на Вашите данни:

Имейл: info@portion.bg
Уебсайт: https://portion.bg/contact

Право на жалба пред надзорен орган:
Комисия за защита на личните данни (КЗЛД)
бул. „Проф. Цветан Лазаров" № 2, 1592 София
Уебсайт: www.cpdp.bg
Имейл: kzld@cpdp.bg

Имате право да подадете жалба и до компетентния надзорен орган в държавата-членка на ЕС, в която пребивавате.`,
  },
];

function Section({ section, index }) {
  const [open, setOpen] = useState(index < 2);
  const Icon = section.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04 }}
      className="border border-[#E6EDF5] rounded-xl overflow-hidden bg-white dark:bg-slate-800"
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 dark:bg-slate-900/70 transition-colors duration-150 group"
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${open ? 'bg-emerald-100' : 'bg-gray-100 dark:bg-slate-700 group-hover:bg-emerald-50'}`}>
            <Icon className={`w-4 h-4 transition-colors duration-200 ${open ? 'text-emerald-600' : 'text-gray-400'}`} />
          </div>
          <span className={`text-[14px] font-semibold transition-colors duration-200 ${open ? 'text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-200'}`}>
            {section.title}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-1 border-t border-gray-100 dark:border-slate-700">
              <div className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed whitespace-pre-line pl-11">
                {section.content}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function PrivacyPage() {
  return (
    <>
      <SEOMeta
        title="Политика за поверителност (GDPR)"
        description="Политика за поверителност на portion.bg. Как обработваме личните Ви данни, Вашите права по GDPR и как да се свържете с нас."
        keywords="portion.bg поверителност, GDPR, лични данни, бисквитки, rights"
        canonical="/privacy"
        breadcrumbs={BREADCRUMBS}
      />
      <PageBreadcrumb items={BREADCRUMBS} />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>

          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-14 h-14 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <ShieldCheck className="w-7 h-7 text-white" />
            </div>
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border mb-4">GDPR</Badge>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3 tracking-tight">Политика за поверителност</h1>
            <p className="text-slate-400 text-sm">Последно обновена: {LAST_UPDATED} · В сила от: {LAST_UPDATED}</p>
          </div>

          {/* GDPR rights quick-access */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            {[
              { icon: Eye, label: 'Достъп', desc: 'Виж данните си' },
              { icon: Trash2, label: 'Изтриване', desc: 'Право да бъдеш забравен' },
              { icon: Download, label: 'Пренос', desc: 'Изтегли данните си' },
              { icon: AlertCircle, label: 'Жалба', desc: 'Свържи се с КЗЛД' },
            ].map(item => (
              <div key={item.label} className="bg-white dark:bg-slate-800 border border-[#E6EDF5] rounded-xl p-3 text-center hover:border-emerald-200 transition-colors duration-150">
                <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <item.icon className="w-4 h-4 text-emerald-600" />
                </div>
                <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">{item.label}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Intro box */}
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-5 py-4 mb-8 flex items-start gap-3">
            <ShieldCheck className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-emerald-800 leading-relaxed">
              portion.bg спазва изискванията на <strong>Регламент (ЕС) 2016/679 (GDPR)</strong> и Закона за защита на личните данни на Република България.
              Вашата поверителност е наш приоритет.
            </p>
          </div>

          {/* Accordion sections */}
          <div className="space-y-2 mb-10">
            {sections.map((s, i) => (
              <Section key={s.id} section={s} index={i} />
            ))}
          </div>

          {/* Footer links */}
          <div className="bg-gray-50 dark:bg-slate-900 rounded-xl border border-[#E6EDF5] p-5 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500 dark:text-slate-400">
            <p>Свързани документи:</p>
            <div className="flex items-center gap-4">
              <Link to="/terms" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:text-gray-200 font-medium transition-colors">
                Общи условия
              </Link>
              <Link to="/disclaimer" className="flex items-center gap-1.5 text-amber-600 hover:text-amber-700 font-medium transition-colors">
                Медицински отказ
              </Link>
            </div>
          </div>

        </motion.div>
      </div>
    </>
  );
}
